/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { View, Text, Icon, Pressable } from '@components/atoms'
import { IconButton, Tooltip, Chip } from '@mui/material'
import { Delete } from '@mui/icons-material'

import { useAppDispatch, orderActions } from '@redux/index'
import {
	blue,
	deepPurple,
	grey,
	indigo,
	orange,
	pink,
	teal,
	yellow,
} from '@mui/material/colors'
import { OrderType } from '@schemas'
import dayjs from 'dayjs'

interface PropsType {
	order: OrderType
	onDelete: () => void
	isSelected: boolean
}

export const OrderListItem = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	const onOrderClick = () => {
		reduxDispatch(orderActions.SELECTED_ORDER(props.order))
	}

	const statusTitle =
		props.order.status === 'new'
			? '신규주문'
			: props.order.status === 'preparing'
			? '배송준비중'
			: props.order.status === 'inTransit'
			? '배송중'
			: props.order.status === 'completed'
			? '완료'
			: props.order.status === 'deleted'
			? '삭제'
			: props.order.status === 'refunded'
			? '환불'
			: '신규주문'

	return (
		<View
			css={style}
			className={`${props.isSelected ? 'selected' : ''} ${props.order.status}`}
		>
			<Pressable
				className={'orderItemButton ' + (props.isSelected ? 'selected' : '')}
				onClick={onOrderClick}
			>
				{props.isSelected && (
					<View className="selectedIndicatorView">
						<Icon name="check-bold" className="selectedIndicatorIcon" />
					</View>
				)}
				<View className="buttonContainer">
					<Tooltip title="Delete">
						<span>
							<IconButton
								size="small"
								onClick={e => {
									e.stopPropagation()
									props.onDelete()
								}}
								disabled={!!props.order.deletedAt}
							>
								<Delete fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</View>

				<View className="contentView">
					<View className="header">
						<Text className="title">{props.order.id}</Text>
						<Chip label={statusTitle} size="small" className="status" />
					</View>

					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							수령인 :
						</Text>
						<Text category="s2" className="contentInfoValue">
							{`${props.order.receiverGivenName} ${props.order.receiverFamilyName}`}
						</Text>
					</View>
					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							배송국가 :
						</Text>
						<Text category="s2" className="contentInfoValue">
							{props.order.country}
						</Text>
					</View>
					<View className="contentInfoView">
						<Text category="p2" hint="500" className="contentInfoLabel">
							주문일자 :
						</Text>
						<Text category="s2" className="contentInfoValue">
							{dayjs(props.order.createdAt).format('YYYY.MM.DD')}
						</Text>
					</View>
					{props.order.deliveryCompletedAt && (
						<View className="contentInfoView">
							<Text category="p2" hint="500" className="contentInfoLabel">
								배송완료일 :
							</Text>
							<Text category="s2" className="contentInfoValue">
								{dayjs(props.order.deliveryCompletedAt).format('YYYY.MM.DD')}
							</Text>
						</View>
					)}
				</View>
			</Pressable>
		</View>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	padding: '0.75rem',
	background: 'white',
	borderRadius: '0.5rem',
	marginBottom: '0.75rem',
	boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
	cursor: 'pointer',
	transition: 'all 0.2s ease-in-out',
	border: '1px solid #e5e7eb',
	borderLeft: '10px solid',

	'.selectedIndicatorView': {
		position: 'absolute',
		width: '1.3rem',
		height: '1.3rem',
		top: 0,
		bottom: 0,
		left: '-0.8rem',
		margin: 'auto',
		background: 'white',
		border: '1px solid',
		borderColor: deepPurple['A400'],
		borderRadius: '50%',

		'.selectedIndicatorIcon': {
			color: deepPurple['A400'],
			fontSize: '18px',
		},
	},

	'.buttonContainer': {
		position: 'absolute',
		top: '0.75rem',
		right: '0.75rem',
		display: 'flex',
		gap: '0.25rem',
	},
	'.contentView': {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',

		'.header': {
			display: 'flex',
			gap: '0.5rem',
			alignItems: 'center',
			paddingRight: '4rem',
			height: '2.25rem',

			'.title': {
				fontWeight: 600,
			},

			'.status': {
				fontSize: '0.75rem',
				fontWeight: 500,

				'&.new': {
					backgroundColor: grey['800'],
					color: 'white',
				},

				'&.preparing': {
					backgroundColor: yellow['A400'],
					color: grey[900],
				},

				'&.inTransit': {
					backgroundColor: teal['A400'],
					color: 'white',
				},

				'&.completed': {
					backgroundColor: blue['A400'],
					color: 'white',
				},

				'&.deleted': {
					backgroundColor: pink['A400'],
					color: 'white',
				},

				'&.refunded': {
					backgroundColor: orange['A400'],
					color: 'white',
				},
			},
		},

		'.contentInfoView': {
			display: 'flex',
			alignItems: 'center',
			gap: '0.25rem',
			height: '1rem',
			marginBottom: '0.5rem',

			'.contentInfoLabel': {
				margin: 0,
				display: 'flex',
				alignItems: 'center',
				gap: '0.25rem',
			},
			'.contentInfoValue': {
				margin: 0,
			},
		},
	},

	'&:hover': {
		backgroundColor: grey[50],
		boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
	},

	'&.new': {
		borderLeftColor: grey['400'],
	},

	'&.preparing': {
		borderLeftColor: yellow['A400'],
		color: yellow['A400'],

		'.title': {
			color: yellow['A400'],
		},

		'.status': {
			backgroundColor: yellow['A400'],
			color: 'white',
		},
	},

	'&.inTransit': {
		borderLeftColor: teal['A400'],
		color: teal['A400'],

		'.title': {
			color: teal['A400'],
		},

		'.status': {
			backgroundColor: teal['A400'],
			color: 'white',
		},
	},

	'&.completed': {
		borderLeftColor: blue['A400'],
		color: blue['A400'],

		'.title': {
			color: blue['A400'],
		},

		'.status': {
			backgroundColor: blue['A400'],
			color: 'white',
		},
	},

	'&.deleted': {
		borderLeftColor: pink['A400'],
		color: pink['A400'],

		'.title': {
			color: pink['A400'],
		},

		'.status': {
			backgroundColor: pink['A400'],
			color: 'white',
		},
	},

	'&.refunded': {
		borderLeftColor: orange['A400'],
		color: orange['A400'],

		'.title': {
			color: orange['A400'],
		},

		'.status': {
			backgroundColor: orange['A400'],
			color: 'white',
		},
	},

	'&.selected': {
		borderColor: deepPurple['A400'],
		borderLeftColor: deepPurple['A400'],
		background: '#eee7f6',

		'.contentView': {
			'.header': {
				'.title': {
					color: deepPurple['A400'],
				},

				'.status': {
					backgroundColor: deepPurple['A400'],
					color: 'white',
				},
			},
		},

		'.contentInfoView': {
			'.contentInfoLabel': {
				color: indigo[500],
			},

			'.contentInfoValue': {
				color: deepPurple['A400'],
			},
		},
	},
})
